import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import './LoginPage.css';
import logo from '../assets/logo-lnv.jpeg';

const LoginPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showResend, setShowResend] = useState(false);
    const [resending, setResending] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !password) {
            setError('Por favor, ingresa tu email y contraseña');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log('[DEBUG] Attempting traditional login for:', email);
            const res = await authService.login({ email, password });
            console.log('[DEBUG] Login successful:', res);

            const user = res.user;
            let from = null;
            if ((location.state as any)?.from?.pathname) {
                from = (location.state as any).from.pathname + ((location.state as any).from.search || '');
            }

            if (from) {
                navigate(from);
            } else if (user.roles?.includes('SUPERUSER')) {
                navigate('/admin/dashboard');
            } else if (user.roles?.includes('BARBER')) {
                navigate('/barber/dashboard');
            } else {
                navigate('/client/dashboard');
            }
        } catch (err: any) {
            console.error('[DEBUG] Login error:', err);
            const message = err.response?.data?.message;

            if (message === 'EMAIL_NOT_VERIFIED') {
                setError('Tu correo electrónico aún no ha sido verificado. Por favor, revisa tu bandeja de entrada.');
                setShowResend(true);
            } else {
                setError(message || 'Email o contraseña incorrectos');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        setResending(true);
        try {
            await authService.resendVerification(email);
            setError('Correo de verificación reenviado con éxito.');
            setShowResend(false);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Error al reenviar el correo.');
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <img src={logo} alt="LNV Logo" className="logo-img" />
                    </div>

                    <div className="login-content">

                        <form className="login-form" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <input
                                    id="email"
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <input
                                    id="password"
                                    type="password"
                                    placeholder="Contraseña"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    disabled={loading}
                                    required
                                />
                            </div>

                            {error && <div className="error-message">{error}</div>}

                            <button
                                type="submit"
                                className="login-submit-btn"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="btn-loading">
                                        <div className="spinner"></div>
                                        <span>Iniciando sesión...</span>
                                    </div>
                                ) : (
                                    'Iniciar Sesión'
                                )}
                            </button>

                            {showResend && (
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ width: '100%', marginTop: '1rem' }}
                                    onClick={handleResendVerification}
                                    disabled={resending}
                                >
                                    {resending ? 'Reenviando...' : 'Reenviar Correo de Verificación'}
                                </button>
                            )}
                        </form>

                        <div className="login-info-section">
                            <div className="info-card highlight">
                                <h3>¿Eres nuevo? ¡Regístrate!</h3>
                                <p>Crea tu cuenta de cliente para reservar turnos.</p>
                                <Link to="/register" state={{ from: (location.state as any)?.from }} style={{ color: 'var(--accent)', fontWeight: 600, marginTop: '8px', display: 'inline-block' }}>
                                    Registrarse →
                                </Link>
                            </div>
                            
                            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                                <Link to="/forgot-password" style={{ color: '#a3a3a3', textDecoration: 'underline', fontSize: '0.9rem' }}>
                                    ¿Olvidaste tu contraseña?
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="login-footer">
                        <p>
                            &copy; 2026 LNV • Desarrollado por{' '}
                            <a
                                href="https://wa.me/543517667366"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="¿Querés un sistema así? Contactame"
                                className="developer-link"
                            >
                                Tap<span className="developer-accent">IA</span> Labs
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
